import { Divider, Stack, StackProps } from '@mui/material';
import {
  EditorContentProps,
  getToolbarButtons,
  KEY_SHORTCUTS_MAC,
  KEY_SHORTCUTS_WINDOWS,
  ToolbarButtonsProps,
} from '@provision/rich-text-editor';
import React, { cloneElement, ReactElement } from 'react';
import { Tooltip } from 'src/components';
import { Platform } from 'src/utils/system';
import { AddLinkPopover, useAddLinkPopover } from './AddLinkPopover';
import { EditorToolbarButton } from './editor-toolbar-button';
import { EditorToolbarButtons } from './editor-toolbar-buttons';
import { StyledDynamicIcon } from './styled-dynamic-icon';

export const ToolbarIcon = ({
  data,
}: {
  data: {
    title: string | ReactElement;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isActive?: boolean;
    disabled?: boolean;
    icon: string;
  };
}) => {
  const { title, onClick, isActive, disabled, icon } = data;

  return (
    <Tooltip title={title} sx={{ whiteSpace: 'pre-wrap', display: 'inline-block' }}>
      <span>
        <EditorToolbarButton onClick={onClick} isActive={isActive} disabled={disabled}>
          <StyledDynamicIcon filled name={icon} />
        </EditorToolbarButton>
      </span>
    </Tooltip>
  );
};

export type ToolbarOptionElement = ReactElement<{ editor: EditorContentProps['editor'] }>;

type EditorToolbarProps = EditorContentProps &
  StackProps & {
    toolbarOptions?: ToolbarOptionElement[];
    withToolBarOtherOptions?: boolean;
    toolbarButtonsProps?: Partial<ToolbarButtonsProps>;
  };

const DEFAULT_TOOLBAR_OPTIONS: ToolbarOptionElement[] = [];

export const EditorToolbar = ({
  editor,
  toolbarOptions = DEFAULT_TOOLBAR_OPTIONS,
  withToolBarOtherOptions,
  toolbarButtonsProps,
  ...rest
}: EditorToolbarProps) => {
  const keyShortcuts = Platform.isMacOS ? KEY_SHORTCUTS_MAC : KEY_SHORTCUTS_WINDOWS;

  const {
    anchorEl: linkPopoverAnchorEl,
    selectedText,
    openPopover: handleAddLink,
    closePopover: handleLinkPopoverClose,
    handleLinkSubmit,
  } = useAddLinkPopover({ editor });

  const { formatting: formattingButtons, other: otherButtons } = getToolbarButtons({
    editor,
    keyShortcuts,
    onLinkClick: handleAddLink,
    ...toolbarButtonsProps,
  });

  if (!editor) {
    return null;
  }

  return (
    <Stack direction="row" sx={{ padding: '6px 0px', gap: '2px' }} {...rest}>
      <AddLinkPopover
        open={!!linkPopoverAnchorEl}
        anchorEl={linkPopoverAnchorEl}
        onClose={handleLinkPopoverClose}
        onSubmit={handleLinkSubmit}
        initialTitle={selectedText}
      />
      <EditorToolbarButtons formattingButtons={formattingButtons} />
      {withToolBarOtherOptions ? (
        <>
          <Divider orientation="vertical" sx={{ margin: '0 0.5rem' }} />
          {otherButtons?.map((button) => <ToolbarIcon key={button.title} data={button} />)}
          {toolbarOptions.map((option) => cloneElement(option, { editor }))}
        </>
      ) : null}
    </Stack>
  );
};
